import React, { useEffect, useRef, useCallback } from 'react';
import Matter from 'matter-js';

const BallCanvas = ({ balls, engine, drawerWidth, boundaries, onSize }) => {
  const canvasRef = useRef(null);
  const renderRef = useRef(null);

  const handleResize = useCallback((w, h) => {
    onSize(w, h);
    const render = renderRef.current;
    if (render) {
      render.canvas.width = w;
      render.canvas.height = h;
      render.options.width = w;
      render.options.height = h;
    }
  }, [onSize]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        handleResize(width, height);
      }
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, [handleResize]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w === 0 || h === 0) return;

    engine.timing.timeScale = 1;
    engine.positionIterations = 100;

    const render = Matter.Render.create({
      element: el,
      engine: engine,
      options: {
        width: w,
        height: h,
        wireframes: false,
        background: '#ffffff'
      }
    });
    renderRef.current = render;

    const mouse = Matter.Mouse.create(render.canvas);
    const mouseConstraint = Matter.MouseConstraint.create(engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: true }
      }
    });

    Matter.World.add(engine.world, mouseConstraint);
    engine.world.gravity.y = 1;

    Matter.Render.run(render);

    return () => {
      Matter.Render.stop(render);
      render.canvas.remove();
      render.textures = {};
      renderRef.current = null;
    };
  }, [engine]);

  useEffect(() => {
    if (engine) {
      const currentBalls = Matter.Composite.allBodies(engine.world);
      currentBalls.forEach(body => {
        if (!body.isStatic && !balls.includes(body)) {
          Matter.World.remove(engine.world, body);
        }
      });
      balls.forEach(ball => {
        if (!currentBalls.includes(ball)) {
          Matter.World.add(engine.world, ball);
        }
      });
    }
  }, [balls, engine, boundaries]);

  return (
    <div
      id="canvas-container"
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#ffffff',
        overflow: 'hidden',
      }}
    />
  );
};

export default BallCanvas;
